import { Injectable, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { AiErrorResponse } from '../models/ai-error-response';

/**
 * Translated error with suggestions
 */
export interface TranslatedAiError {
  message: string;
  suggestions?: string[];
}

/**
 * Service for translating AI extraction errors from backend
 * Maps error codes to i18n translation keys
 */
@Injectable({
  providedIn: 'root',
})
export class AiErrorTranslatorService {
  private transloco = inject(TranslocoService);

  /**
   * Translate an AI extraction error using i18n
   * @param error - The error response from backend
   * @returns Translated error message and suggestions
   */
  translateError(error: AiErrorResponse): TranslatedAiError {
    const context = error.context || {};

    // Map error code to translation key
    const translationKey = `aiErrors.${error.code}`;

    // Get translated message with context interpolation
    const message = this.transloco.translate(translationKey, context);

    // Extract suggestions from context if available
    const suggestions = this.extractSuggestions(error);

    return {
      message,
      suggestions,
    };
  }

  /**
   * Extract and translate suggestions from error context
   * @param error - The error response
   * @returns Array of translated suggestion strings
   */
  private extractSuggestions(error: AiErrorResponse): string[] | undefined {
    const context = error.context || {};
    const suggestions: string[] = [];

    // Extract validation message as suggestion
    if (context['validationMessage']) {
      suggestions.push(context['validationMessage']);
    }

    // Extract warnings array
    if (Array.isArray(context['warnings']) && context['warnings'].length > 0) {
      suggestions.push(...context['warnings']);
    }

    // For unsupported format error, list supported formats
    if (error.code === 'UNSUPPORTED_FILE_FORMAT' && Array.isArray(context['supportedFormats'])) {
      const formats = context['supportedFormats'].join(', ').toUpperCase();
      suggestions.push(
        this.transloco.translate('aiErrors.supportedFormatsHint', { formats })
      );
    }

    return suggestions.length > 0 ? suggestions : undefined;
  }

  /**
   * Check if an HTTP error is an AI extraction error
   * @param error - HTTP error object
   * @returns True if error is an AI extraction error
   */
  isAiExtractionError(error: unknown): boolean {
    return (
      typeof error === 'object' &&
      error !== null &&
      'error' in error &&
      typeof error.error === 'object' &&
      error.error !== null &&
      'code' in error.error &&
      'status' in error.error &&
      'context' in error.error
    );
  }

  /**
   * Get confidence score from error context
   * @param error - The error response
   * @returns Confidence score (0-100) or null
   */
  getConfidenceScore(error: AiErrorResponse): number | null {
    return error.context?.['confidenceScore'] ?? null;
  }

  /**
   * Get validation status from error context
   * @param error - The error response
   * @returns Validation status ('valid', 'uncertain', 'invalid') or null
   */
  getValidationStatus(error: AiErrorResponse): string | null {
    return error.context?.['validationStatus'] ?? null;
  }
}
